import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { BookOpen, Briefcase, Sparkles, GraduationCap, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

export function LearnAndEarn() {
  const navigate = useNavigate();

  const handleCustomerWorkClick = () => {
    navigate('/learn/customer-work');
  };

  const handleWorkshopClick = () => {
    navigate('/learn/workshop');
  };

  const handleCurriculumClick = () => {
    navigate('/learn/curriculum');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-orange-100 to-red-100 mobile-container relative overflow-hidden mobile-landscape:min-h-auto mobile-landscape:py-4">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-48 h-48 xs:w-64 xs:h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-orange-300/40 rounded-full blur-[100px] mobile-landscape:w-32 mobile-landscape:h-32"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 xs:w-64 xs:h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 bg-red-300/40 rounded-full blur-[100px] mobile-landscape:w-32 mobile-landscape:h-32"></div>

      <div className="max-w-7xl mx-auto relative z-10 py-16 sm:py-20 md:py-24 mobile-landscape:py-4">

        {/* Back to Main Page Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="px-4 mb-4 pt-4 sm:pt-0"
        >
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/70 hover:bg-white border border-orange-200 hover:border-orange-400 text-orange-700 hover:text-orange-900 font-semibold text-sm shadow-sm hover:shadow-md transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Main Page
          </button>
        </motion.div>
        {/* Header Section */}
        <motion.div
          className="text-center mb-8 sm:mb-10 md:mb-12 mobile-landscape:mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="mb-3 sm:mb-4 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mobile-heading sm:text-4xl md:text-5xl font-bold mobile-landscape-heading">
            Learn & Earn
          </h1>
          <p className="text-orange-700 mobile-text lg:text-lg max-w-2xl mx-auto mobile-landscape-text font-medium">
            Transform your skills into income or get your work done professionally. Choose your path below!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 px-4">
          {/* Customer Work Portal */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="h-full"
          >
            <Card
              className="cursor-pointer hover:shadow-2xl transition-all duration-300 border-border bg-gradient-to-br from-cyan-50 to-orange-50 hover:border-destructive/50 overflow-hidden group hover:scale-[1.02] active:scale-95 h-full mobile-touch-target"
              onClick={handleCustomerWorkClick}
            >
              <div className="absolute top-0 right-0 w-20 h-20 xs:w-24 xs:h-24 sm:w-32 sm:h-32 bg-orange-400/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500 mobile-landscape:w-16 mobile-landscape:h-16"></div>
              <CardHeader className="relative p-4 sm:p-6">
                <div className="w-12 h-12 xs:w-16 xs:h-16 sm:w-20 sm:h-20 bg-destructive rounded-2xl flex items-center justify-center mb-3 sm:mb-4 mx-auto shadow-lg group-hover:rotate-6 transition-transform duration-300 mobile-landscape:w-10 mobile-landscape:h-10 mobile-landscape:mb-2">
                  <BookOpen className="w-6 h-6 xs:w-8 xs:h-8 sm:w-10 sm:h-10 text-destructive-foreground mobile-landscape:w-5 mobile-landscape:h-5" />
                </div>
                <CardTitle className="text-center text-lg xs:text-xl sm:text-2xl text-foreground mobile-landscape:text-base">
                  Customer Work
                </CardTitle>
                <CardDescription className="text-center mobile-text text-muted-foreground mobile-landscape-text">
                  Submit your assignment or journal work requests
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-destructive rounded-full"></div>
                    <span className="text-xs sm:text-sm text-foreground mobile-landscape:text-xs">Submit assignment work</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                    <span className="text-xs sm:text-sm text-foreground mobile-landscape:text-xs">Request journal writing</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-destructive rounded-full"></div>
                    <span className="text-xs sm:text-sm text-foreground mobile-landscape:text-xs">Set deadlines and pricing</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                    <span className="text-xs sm:text-sm text-foreground mobile-landscape:text-xs">Upload your requirements</span>
                  </div>
                </div>
                <div className="mt-4 sm:mt-6 text-center mobile-landscape:mt-3">
                  <div className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-accent group-hover:text-accent/80 transition-colors mobile-landscape:text-xs">
                    Get Started <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* A.G.O Workshop Portal */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="h-full"
          >
            <Card
              className="cursor-pointer hover:shadow-2xl transition-all duration-300 border-border bg-gradient-to-br from-cyan-50 to-blue-50 hover:border-cyan-500/50 overflow-hidden group hover:scale-[1.02] active:scale-95 h-full mobile-touch-target"
              onClick={handleWorkshopClick}
            >
              <div className="absolute top-0 right-0 w-20 h-20 xs:w-24 xs:h-24 sm:w-32 sm:h-32 bg-blue-400/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500 mobile-landscape:w-16 mobile-landscape:h-16"></div>
              <CardHeader className="relative p-4 sm:p-6">
                <div className="w-12 h-12 xs:w-16 xs:h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mb-3 sm:mb-4 mx-auto shadow-lg group-hover:rotate-6 transition-transform duration-300 mobile-landscape:w-10 mobile-landscape:h-10 mobile-landscape:mb-2">
                  <Briefcase className="w-6 h-6 xs:w-8 xs:h-8 sm:w-10 sm:h-10 text-white mobile-landscape:w-5 mobile-landscape:h-5" />
                </div>
                <CardTitle className="text-center text-lg xs:text-xl sm:text-2xl text-cyan-600 mobile-landscape:text-base">
                  A.G.O Workshop
                </CardTitle>
                <CardDescription className="text-center mobile-text text-muted-foreground mobile-landscape-text">
                  Browse and apply for available work opportunities
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-cyan-500 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-muted-foreground mobile-landscape:text-xs">Browse work opportunities</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-muted-foreground mobile-landscape:text-xs">Apply for assignments</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-cyan-500 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-muted-foreground mobile-landscape:text-xs">Earn through skills</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-muted-foreground mobile-landscape:text-xs">Track your progress</span>
                  </div>
                </div>
                <div className="mt-4 sm:mt-6 text-center mobile-landscape:mt-3">
                  <div className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-cyan-600 group-hover:text-cyan-700 transition-colors mobile-landscape:text-xs">
                    Start Working <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Curriculum Activities Portal */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="h-full"
          >
            <Card
              className="cursor-pointer hover:shadow-2xl transition-all duration-300 border-border bg-gradient-to-br from-cyan-50 to-green-50 hover:border-emerald-500/50 overflow-hidden group hover:scale-[1.02] active:scale-95 h-full mobile-touch-target"
              onClick={handleCurriculumClick}
            >
              <div className="absolute top-0 right-0 w-20 h-20 xs:w-24 xs:h-24 sm:w-32 sm:h-32 bg-green-400/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500 mobile-landscape:w-16 mobile-landscape:h-16"></div>
              <CardHeader className="relative p-4 sm:p-6">
                <div className="w-12 h-12 xs:w-16 xs:h-16 sm:w-20 sm:h-20 bg-emerald-500 rounded-2xl flex items-center justify-center mb-3 sm:mb-4 mx-auto shadow-lg group-hover:rotate-6 transition-transform duration-300 mobile-landscape:w-10 mobile-landscape:h-10 mobile-landscape:mb-2">
                  <GraduationCap className="w-6 h-6 xs:w-8 xs:h-8 sm:w-10 sm:h-10 text-white mobile-landscape:w-5 mobile-landscape:h-5" />
                </div>
                <CardTitle className="text-center text-lg xs:text-xl sm:text-2xl text-foreground mobile-landscape:text-base">
                  Curriculum Activities
                </CardTitle>
                <CardDescription className="text-center mobile-text text-muted-foreground mobile-landscape-text">
                  Structured learning modules and skill development
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-muted-foreground mobile-landscape:text-xs">Interactive learning modules</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                    <span className="text-xs sm:text-sm text-muted-foreground mobile-landscape:text-xs">Skill assessments</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-xs sm:text-sm text-muted-foreground mobile-landscape:text-xs">Progress tracking</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-muted rounded-lg backdrop-blur-sm border border-border">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full"></div>
                    <span className="text-xs sm:text-sm text-muted-foreground mobile-landscape:text-xs">Certification paths</span>
                  </div>
                </div>
                <div className="mt-4 sm:mt-6 text-center mobile-landscape:mt-3">
                  <div className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-accent group-hover:text-accent/80 transition-colors mobile-landscape:text-xs">
                    Start Learning <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default LearnAndEarn;
